<template>
    <nav class="gmf-customer-card-navigation" :class="classes" tabindex="0">
        <FullBodyAvatar v-if="use3DAvatar" :customer="profile" :paused="pauseAvatar" :touchTarget="$refs.touchTarget"
            animated :customizable="isMyProfile" @customize="$emit('openSettings')" @touchstart.stop />

        <SideNavItem v-for="item in menuItems" :key="item.name" :showIcons="showIcons" :item="item"
            :selectedItem="selectedItem" @clickItem="handleItemClick" />
    </nav>
</template>

<script>
import state from '@/state';
import FullBodyAvatar from '@/components/FullBodyAvatar';
import SideNavItem from '@/components/SideNav/SideNavItem';
import openTokenShop from '@/components/TokenShop/openTokenShop';
import openSocialModal from '@/widgets/SocialFeed/openSocialModal';
import callApi from '@/utils/callApi';

const t = state.t.prefix('profile_tabs');

export default {
    name: 'CustomerCardNavigation',

    components: {
        FullBodyAvatar,
        SideNavItem
    },

    props: {
        profile: {
            type: Object,
            required: true
        },
        selectedItem: {
            type: Object,
            default: null
        },
        detached: {
            type: Boolean,
            default: false
        },
        side: {
            type: String,
            default: 'left'
        },
        closed: {
            type: Boolean,
            default: false
        },
        hideAvatar: {
            type: Boolean,
            default: false
        },
        pauseAvatar: {
            type: Boolean,
            default: false
        },
    },

    data() {
        return {
            state,
            //expanded: false,
            // selectedItem: null,
            windowWidth: window.innerWidth,
            links: [],
            firstFrame: true,
        };
    },

    computed: {
        classes() {
            const cls = {};
            if (this.detached) {
                cls['gmf-customer-card-navigation-detached'] = true;
                cls['gmf-customer-card-navigation-detached-' + this.side] = true;
                cls['gmf-customer-card-navigation-detached-' + this.side + '-closed'] = this.closed || this.firstFrame;
            }
            return cls;
        },
        isDesktop() {
            return this.windowWidth > 1000;
        },

        isMyProfile() {
            return (state.profile && this.profile && state.profile.id === this.profile.id);
        },

        use3DAvatar() {
            if (this.hideAvatar) return false;
            return state.merchantConfig.avatars.enabled === '3D';
        },

        showIcons() {
            return state.merchantConfig.widgets?.customerCardIcons?.enabled;
        },

        showDMs() {
            return state.merchantConfig.directMessages.enabled;
        },

        showTokenShop() {
            return false;//state.merchantConfig.tokenShop?.enabled;
        },

        showInventory() {
            return state.merchantConfig.inventory?.enabled;
        },

        myPagesUrl() {
            return state.merchantConfig.notifications?.avatar?.myPagesUrl;
        },

        menuItems() {
            const items = [];
            const showQuests = state.merchantConfig.quests.enabled || false;
            let showAchievements = state.merchantConfig.achievements?.enabled || false;
            if (! this.isMyProfile && state.merchantConfig.achievements?.hideFromOthers) {
                showAchievements = false;
            }

            // Profile
            items.push({
                value: 'profile',
                name: this.profile?.name || t('profile'),
                icon: 'my-pages',
                hideHeader: true,
            });

            // Notifications (for logged in users)
            if (this.isMyProfile) {
                items.push({
                    value: 'notifications',
                    name: t('notifications'),
                    icon: 'tab-notification',
                    hideItem: true,
                });
            }

            // Direct Messages
            if (this.showDMs && this.isMyProfile) {
                items.push({
                    value: 'direct_messages',
                    name: t('direct_messages'),
                    icon: 'tab-direct-message',
                });
            }

            // Quests
            if (showQuests && this.isMyProfile) {
                items.push({
                    value: 'quests',
                    name: t('quests'),
                    icon: 'tab-quest',
                    hideHeader: true,
                });
            }

            // Achievements
            if (showAchievements) {
                items.push({
                    value: 'achievements',
                    name: t('achievements'),
                    icon: 'tab-achievement',
                });
            }

            // Ordered Products
            if (this.isMyProfile) {
                items.push({
                    value: 'ordered_products',
                    name: t('ordered_products'),
                    icon: 'tab-products',
                    hideHeader: true,
                });
            }

            // My Content
            items.push({
                value: 'my_content',
                name: this.isMyProfile ? t('my_content') : t('my_content_public'),
                icon: 'tab-my-content',
            });

            // Inventory
            if (this.showInventory) {
                items.push({
                    value: 'inventory',
                    name: this.isMyProfile ? t('inventory') : t('inventory_public'),
                    icon: 'emoji',
                });
            }

            items.push({ isSpacer: true });

            // Customer Settings
            if (this.isMyProfile) {
                items.push({
                    value: 'customer_settings',
                    name: t('customer_attributes'),
                    icon: 'change_avatar',
                });
            }

            // Token Shop
            if (this.showTokenShop && this.isMyProfile) {
                items.push({
                    value: 'token_shop',
                    name: t('token_shop'),
                    icon: 'token-shop',
                    isButton: false,
                    action: openTokenShop
                });
            }

            const linksUnderMypages = false;//state.merchantConfig.profileCard?.links?.showUnderMyPagesBtn;

            const linkItems = this.links.map(link => ({
                value: link.key,
                name: state.t('profile_links.' + link.key + '.label') || link.key,
                icon: link.icon,
                isButton: linksUnderMypages,
                url: link.url,
            }));

            // My Pages URL
            if (this.myPagesUrl && this.isMyProfile) {
                items.push({
                    value: 'my_pages',
                    name: t('my_pages'),
                    icon: 'my-pages',
                    //isButton: true,
                    action: () => {
                        window.open(this.myPagesUrl, "_self");
                    },
                    //children: linksUnderMypages ? linkItems : null,
                });
            }
            if (linkItems.length > 0) {
                items.push(...linkItems);
            }

            // Social Feed
            if(state.merchantConfig?.socialFeed?.enabled) {
                items.push({
                    value: 'social_feed',
                    name: t('social_feed'),
                    icon: 'tab-forum',
                    isButton: true,
                    action: () => {
                        openSocialModal({ displayHeader: true });
                        // this.expanded = false;
                    },
                });
            }

            return items;
        }
    },

    methods: {
        /*toggleExpanded() {
            this.expanded = !this.expanded;
        },*/

        handleItemClick(item, evt) {
            //if (this.expanded || this.isDesktop) {
            if (item.action) {
                item.action(evt);
            } else {
                this.$emit('item-selected', item);
            }
            // this.expanded = false;
            //} else {
            // this.expanded = true;
            //}
        },

        updateWindowWidth() {
            this.windowWidth = window.innerWidth;
        },

        /*async loadProfile() {
            if (!this.customerId) {
                this.profile = state.profile;
                return;
            }

            const res = await callApi('GET', '/v1/client/profile/' + this.customerId);
            this.profile = await res.json();
        },*/

        async loadProfileLinks() {
            if (!this.isMyProfile) return;

            const res = await callApi('GET', '/v1/profile-links');
            const { links } = await res.json();
            this.links = links;
        }
    },

    async mounted() {
        setTimeout(() => {
            this.firstFrame = false;
            this.$el.focus(); // for keyboard navigation
        }, 0);
        window.addEventListener('resize', this.updateWindowWidth);
        //await this.loadProfile();
        await this.loadProfileLinks();
    },

    beforeUnmount() {
        window.removeEventListener('resize', this.updateWindowWidth);
    },

    watch: {
        customerId: {
            handler: 'loadProfile',
            immediate: true
        }
    }
};
</script>

<style>
.gmf-customer-card-navigation {
    z-index: 2;

    background: var(--gmf-sidemenu-background-color);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    overflow-y: auto;
    width: 250px;
    max-width: 100%;

    padding-bottom: var(--gmf-p-3);
}

.gmf-customer-card-navigation:not(:has(.gmf-fullbody-avatar)) {
    padding-top: var(--gmf-p-4);
}

.gmf-customer-card-navigation:focus-visible {
    outline: none !important;
    border: none !important;
}

.gmf-context .gmf-customer-card-navigation-detached {
    position: fixed;
    top: 0;
    bottom: 0;
    width: 250px;

    transition: transform 300ms ease-out;
    z-index: var(--gmf-modal-z-index);
}

.gmf-customer-card-navigation-detached-left {
    left: 0;
}

.gmf-customer-card-navigation-detached-right {
    right: 0;
}

.gmf-customer-card-navigation-detached-left-closed {
    transform: translateX(-100%);
}

.gmf-customer-card-navigation-detached-right-closed {
    transform: translateX(100%);
}

/*
.gmf-customer-card-expandable {
    background: var(--gmf-sidemenu-background-color);
    padding: 0;
    right: 0;
    flex-direction: column;
    display: flex;
    cursor: pointer;
    overflow: hidden;
    transition: max-width 300ms ease-in-out;
    will-change: width;
    overflow-y: auto;
    height: 100%;
}*/


/*@media (max-width: 1000px) {
    .gmf-customer-card-navigation {
        filter: drop-shadow(4px 0px 4px rgba(0, 0, 0, 0.2));
    }

    .gmf-customer-card-navigation .gmf-fullbody-avatar {
        height: 220px;
        width: 300px;
        max-width: unset;
        flex-shrink: 0;
    }
}*/
</style>